import {
  AnalyticEvent,
  BundleChecklistFragment,
  formatPrice,
  isWeb,
  usePostHog,
} from "@stepapp/core";
import {
  Block,
  Gradient,
  ImageBackground,
  palette,
  spacing,
  Touchable,
  Typography,
  useBreakpoints,
} from "@stepapp/ui";
import { useRouter } from "expo-router";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Linking, StyleSheet } from "react-native";
import { AppStorage } from "../../src/services/AppStorage";

type Props = {
  testID?: string;
  id: string;
  slug?: string;
  isBanner: boolean;
  bannerRedirection: string;
  name: string;
  checkList?: BundleChecklistFragment[];
  desktopBanner: string;
  mobileBanner: string;
  startingPrice: number;
  categoryItem?: boolean;
  spacious?: boolean;
};

const ServiceItem = ({
  id,
  name,
  slug,
  isBanner,
  bannerRedirection,
  checkList = [],
  desktopBanner,
  mobileBanner,
  startingPrice,
  testID,
  categoryItem = false,
  spacious = false,
}: Props) => {
  const postHog = usePostHog();
  const { t } = useTranslation();
  const { push, navigate } = useRouter();
  const { mobileOnly, sm, md } = useBreakpoints();

  const priceFormatter = formatPrice(AppStorage().get("countryCode"));
  const formattedPrice = priceFormatter(startingPrice);

  const height = spacing.unit * (md ? 24 : categoryItem ? 18 : 20);
  const showPrice = startingPrice !== 0;

  const handleNavigate = useCallback(() => {
    const hasBanner = isBanner && bannerRedirection;

    if (hasBanner) {
      return Linking.openURL(bannerRedirection);
    }

    if (categoryItem) {
      return push({
        pathname: "/category",
        params: { id },
      });
    }

    const hasChecklist = checkList.length > 0;
    const checklistPath = mobileOnly && hasChecklist ? "/checklist" : isWeb ? "/configure" : "";

    postHog?.capture(AnalyticEvent.Reservation.Start, { slug: slug || "" });

    return navigate(`/service/${slug}${checklistPath}/`);
  }, [
    push,
    bannerRedirection,
    checkList,
    mobileOnly,
    slug,
    navigate,
    isBanner,
    id,
    categoryItem,
    postHog,
  ]);

  return (
    <Touchable onPress={handleNavigate} style={styles.image} testID={testID}>
      <ImageBackground
        contentContainerStyle={spacious ? styles.imageContainerSpacious : styles.imageContainer}
        contentFit={isWeb ? "fill" : "cover"}
        imageStyle={styles.image}
        source={sm ? desktopBanner : mobileBanner}
        style={[{ height, marginTop: mobileOnly ? 16 : 24 }]}
      >
        <Gradient direction="left" height={height} style={styles.image} width="100%" />

        <Block>
          <Typography shadow type="white" variant="sectionTitle">
            {name}
          </Typography>

          {showPrice && (
            <Typography shadow type="white">
              {`${t("common.from")} ${formattedPrice}`}
            </Typography>
          )}
        </Block>

        {!categoryItem && (
          <Block flex={1} style={styles.footer}>
            <Block style={styles.button}>
              <Typography type="white" variant="button">
                {t("screens.dashboard.bookNow")}
              </Typography>
            </Block>
          </Block>
        )}
      </ImageBackground>
    </Touchable>
  );
};

export default React.memo(ServiceItem);

const styles = StyleSheet.create({
  button: {
    backgroundColor: palette.primary.accent,
    borderRadius: spacing.radius,
    paddingHorizontal: spacing.unit * 2,
    padding: spacing.unit,
  },
  imageContainerSpacious: {
    paddingLeft: spacing.unit * 3.5,
    paddingVertical: spacing.unit * 2.75,
  },
  imageContainer: {
    paddingLeft: spacing.unit * 3,
    paddingVertical: spacing.unit * 2,
  },
  footer: {
    alignItems: "flex-start",
    justifyContent: "flex-end",
  },
  image: {
    borderRadius: spacing.radius,
  },
});
